import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../services/api';
import {
  clearOfflineHistory,
  flushOfflineQueue,
  loadOfflineHistory,
  loadOfflineQueue,
  offlineQueueCount,
  removeOfflinePedido,
} from '../utils/offlinePedidoQueue';

/**
 * Sincroniza pedidos guardados sin internet cuando vuelve la conexión.
 */
export default function useOfflinePedidoSync({ onSynced, enabled = true, vendedorId = null } = {}) {
  const [pending, setPending] = useState(() => offlineQueueCount(vendedorId));
  const [queue, setQueue] = useState(() => loadOfflineQueue(vendedorId));
  const [history, setHistory] = useState(() => loadOfflineHistory(vendedorId));
  const [syncing, setSyncing] = useState(false);
  const [online, setOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const syncingRef = useRef(false);
  const onSyncedRef = useRef(onSynced);
  onSyncedRef.current = onSynced;

  const refresh = useCallback(() => {
    setPending(offlineQueueCount(vendedorId));
    setQueue(loadOfflineQueue(vendedorId));
    setHistory(loadOfflineHistory(vendedorId));
  }, [vendedorId]);

  const sync = useCallback(async ({ includeHeld = false } = {}) => {
    if (!enabled || syncingRef.current) return { sent: 0 };
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      setOnline(false);
      refresh();
      return { sent: 0 };
    }
    if (!offlineQueueCount(vendedorId)) {
      refresh();
      return { sent: 0 };
    }

    syncingRef.current = true;
    setSyncing(true);
    try {
      const result = await flushOfflineQueue((payload) => api.crearPedido(payload), {
        vendedorId,
        includeHeld: Boolean(includeHeld),
      });
      refresh();
      if (result.sent.length) {
        onSyncedRef.current?.(result.sent);
      }
      return { sent: result.sent.length, remaining: result.remaining };
    } finally {
      syncingRef.current = false;
      setSyncing(false);
      refresh();
    }
  }, [enabled, refresh, vendedorId]);

  useEffect(() => {
    refresh();
    const onOnline = () => {
      setOnline(true);
      sync();
    };
    const onOffline = () => {
      setOnline(false);
      refresh();
    };
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    const t = setInterval(() => {
      refresh();
      if (
        typeof navigator !== 'undefined' &&
        navigator.onLine &&
        offlineQueueCount(vendedorId)
      ) {
        sync();
      }
    }, 45000);
    if (typeof navigator === 'undefined' || navigator.onLine) {
      sync();
    }
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      clearInterval(t);
    };
  }, [refresh, sync, vendedorId]);

  const clearHistory = useCallback(() => {
    clearOfflineHistory(vendedorId);
    refresh();
  }, [refresh, vendedorId]);

  const removePedido = useCallback(
    (id) => {
      if (!id) return;
      removeOfflinePedido(id);
      refresh();
      try {
        window.dispatchEvent(new CustomEvent('oh-offline-queue-changed'));
      } catch {
        /* ignore */
      }
    },
    [refresh]
  );

  return {
    pending,
    syncing,
    online,
    queue,
    history,
    /** Auto: solo red-fallidos. Manual: también los de «Guardar en tablet». */
    syncNow: () => sync({ includeHeld: true }),
    refresh,
    clearHistory,
    removePedido,
  };
}
